import * as React from "react"
import { useMemo, useState } from "react"
import { Badge, Box, Skeleton, Typography } from "@mui/material"
import { format } from "date-fns"
import { ButtonIconSeeker, IconSeeker, PopoverSeeker } from "@ui-seeker/core"

// By path, not through the @components barrel, which also exports this component.
import AnnouncementDialog from "@components/AnnouncementDialog"
import AnnouncementFrame, {
    ANNOUNCEMENT_BRAND_SURFACE,
    ANNOUNCEMENT_SURFACE_SHADOW,
} from "@components/AnnouncementFrame"
import { useAnnouncementsHistory } from "@hooks"
import { announcementColors } from "Theme/palette"
import {
    formatRelativeDate,
    relativeDateGroup,
    RELATIVE_GROUP_TODAY,
    RELATIVE_GROUP_WEEK,
    RELATIVE_GROUP_EARLIER,
} from "@utils"

/**
 * The bell in the header: past announcements, read or not.
 *
 * It goes in through the layout's `components.headerTools` slot rather than by touching
 * `@ui-seeker/app`. The layout already draws a decorative bell of its own, switched off in
 * this app (`Layout/config.js`, `showNotifications: false`) and with no click handler — so
 * it is not reused.
 *
 * Unlike the popup, this surface must be ignorable: it is consulted, never imposed.
 *
 * The shape follows what notification inboxes have converged on (Knock, MagicBell, Novu,
 * and the GitHub/Slack/Linear family): header with the count and a bulk action, an All /
 * Unread filter, rows that lead with an icon and close with a relative timestamp, the
 * per-row action revealed on hover instead of parked permanently on screen, skeletons
 * shaped like the real rows, and an empty state that says "you are done" rather than
 * showing an empty box.
 */

const GROUP_ORDER = [RELATIVE_GROUP_TODAY, RELATIVE_GROUP_WEEK, RELATIVE_GROUP_EARLIER]

const FILTER_ALL = "all"
const FILTER_UNREAD = "unread"

const HOVER_BG = '#F4F6F8'

/** Entries arrive newest first, so keeping that order within each bucket is enough. */
function groupByRecency(entries) {
    const buckets = new Map(GROUP_ORDER.map(name => [name, []]))
    entries.forEach(entry => buckets.get(relativeDateGroup(new Date(entry.startAt))).push(entry))
    return GROUP_ORDER
        .map(name => ({ name, items: buckets.get(name) }))
        .filter(group => group.items.length > 0)
}

/** Holds the layout still while loading instead of collapsing the panel to one line of
 *  text and then pushing everything down when the rows arrive. */
function LoadingRows() {
    return (
        <Box sx={{ px: 2, py: 1 }}>
            {[0, 1, 2].map(row => (
                <Box key={row} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, py: 1.25 }}>
                    <Skeleton variant="rounded" width={30} height={30} sx={{ borderRadius: '9px' }} />
                    <Box sx={{ flex: 1 }}>
                        <Skeleton variant="text" width={`${70 - row * 12}%`} height={16} />
                        <Skeleton variant="text" width={54} height={12} />
                    </Box>
                </Box>
            ))}
        </Box>
    )
}

function FilterTab({ label, count, isActive, onClick }) {
    return (
        <Box
            onClick={onClick}
            sx={{
                display: 'flex', alignItems: 'center', gap: 0.75,
                px: 1.25, py: 0.5, borderRadius: '8px', cursor: 'pointer',
                fontSize: 12.5, fontWeight: 600,
                color: isActive ? announcementColors.linkHover : announcementColors.inkMuted,
                backgroundColor: isActive ? HOVER_BG : 'transparent',
                '&:hover': { backgroundColor: HOVER_BG },
            }}
        >
            {label}
            {count > 0 && (
                <Box
                    sx={{
                        px: 0.75, borderRadius: '9px', fontSize: 11, fontWeight: 700,
                        color: isActive ? '#fff' : announcementColors.inkMuted,
                        backgroundColor: isActive
                            ? announcementColors.linkHover
                            : announcementColors.border,
                    }}
                >
                    {count}
                </Box>
            )}
        </Box>
    )
}

export default function AnnouncementsBell() {
    const { entries, unread, isLoading, reload, open, markSeen, markAllSeen } = useAnnouncementsHistory()
    const [anchor, setAnchor] = useState(null)
    const [reading, setReading] = useState(null)
    const [filter, setFilter] = useState(FILTER_ALL)

    const visible = useMemo(
        () => (filter === FILTER_UNREAD ? entries.filter(entry => !entry.seen) : entries),
        [entries, filter],
    )
    const groups = useMemo(() => groupByRecency(visible), [visible])

    const handleOpenEntry = entry => {
        setAnchor(null)
        open(entry.announcementID)
            .then(detail => setReading(detail))
            .catch(() => {})
    }

    return (
        <>
            <Badge
                badgeContent={unread}
                color="error"
                overlap="circular"
                sx={{ '& .MuiBadge-badge': { fontSize: 10, height: 16, minWidth: 16 } }}
            >
                {/* Bare icon in contrast text, exactly like `DownloadManualButton` beside it
                    and the layout's own `ToolbarButton`. DIP Insights' header is **dark**
                    (`MaximalLayout`: `background.black`), so the boxed white button this was
                    ported from — right on Retail's light header — would read here as a white
                    tile stuck next to a bare icon. */}
                <ButtonIconSeeker
                    icon="bell"
                    iconWeight="regular"
                    tooltip="News"
                    // Refreshed on open: the list is loaded once on mount, and anything
                    // published while the tab stayed open would otherwise be missing.
                    onClick={event => { setAnchor(event.currentTarget); reload() }}
                    sx={{ color: "text.contrastText" }}
                />
            </Badge>

            {/* PopoverSeeker and not MUI's Popover: it is what the user menu next to this
                bell uses, so both share the pointer arrow, the radius and the shadow. */}
            <PopoverSeeker
                anchorEl={anchor}
                onClose={() => setAnchor(null)}
                slotProps={{
                    paper: {
                        sx: {
                            width: 380, maxHeight: 460, p: 0,
                            borderRadius: '14px', overflow: 'hidden',
                            boxShadow: ANNOUNCEMENT_SURFACE_SHADOW,
                        },
                    },
                }}
            >
                {/* Brand header, so the panel is recognisably part of the same feature as
                    the popup. Sticky: scrolling a long history must not take the title, the
                    count or "mark all as read" off screen. */}
                <Box
                    sx={{
                        px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1,
                        position: 'sticky', top: 0, zIndex: 3,
                        background: ANNOUNCEMENT_BRAND_SURFACE,
                    }}
                >
                    <IconSeeker icon="bullhorn" sx={{ fontSize: 14, color: 'rgba(255,255,255,0.9)' }} />
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>News</Typography>
                    {unread > 0 && (
                        <Typography
                            onClick={markAllSeen}
                            sx={{
                                ml: 'auto', cursor: 'pointer', fontSize: 12,
                                color: 'rgba(255,255,255,0.75)',
                                '&:hover': { color: '#fff', textDecoration: 'underline' },
                            }}
                        >
                            Mark all as read
                        </Typography>
                    )}
                </Box>

                {/* All / Unread. The count lives on the tab, which is where someone looks
                    when deciding whether the panel is worth reading at all. */}
                <Box
                    sx={{
                        display: 'flex', gap: 0.5, px: 1.5, py: 1,
                        position: 'sticky', top: 45, zIndex: 2, backgroundColor: '#fff',
                        borderBottom: `1px solid ${announcementColors.border}`,
                    }}
                >
                    <FilterTab
                        label="All"
                        count={entries.length}
                        isActive={filter === FILTER_ALL}
                        onClick={() => setFilter(FILTER_ALL)}
                    />
                    <FilterTab
                        label="Unread"
                        count={unread}
                        isActive={filter === FILTER_UNREAD}
                        onClick={() => setFilter(FILTER_UNREAD)}
                    />
                </Box>

                {isLoading && entries.length === 0 && <LoadingRows />}

                {!isLoading && visible.length === 0 && (
                    <Box sx={{ px: 3, py: 4, textAlign: 'center' }}>
                        <Box
                            sx={{
                                width: 48, height: 48, mx: 'auto', mb: 1.5, borderRadius: '14px',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                backgroundColor: HOVER_BG,
                                border: `1px solid ${announcementColors.border}`,
                            }}
                        >
                            <IconSeeker
                                icon={filter === FILTER_UNREAD ? "check-double" : "bullhorn"}
                                sx={{ fontSize: 18, color: announcementColors.link }}
                            />
                        </Box>
                        <Typography sx={{ fontSize: 14, fontWeight: 600, color: announcementColors.inkStrong }}>
                            {filter === FILTER_UNREAD ? "You're all caught up" : "No news yet"}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: '#9CA3AF', mt: 0.25 }}>
                            {filter === FILTER_UNREAD
                                ? "Nothing left to read."
                                : "Product updates will show up here."}
                        </Typography>
                    </Box>
                )}

                {groups.map(group => (
                    <Box key={group.name}>
                        <Typography
                            sx={{
                                display: 'block', px: 2, pt: 1.5, pb: 0.5,
                                color: '#9CA3AF', fontWeight: 600, fontSize: 11,
                                textTransform: 'uppercase', letterSpacing: '.04em',
                                // Under the header and the tabs, so the group a row belongs
                                // to stays on screen while scrolling through it.
                                position: 'sticky', top: 87, zIndex: 1, backgroundColor: '#fff',
                            }}
                        >
                            {group.name}
                        </Typography>

                        {group.items.map(entry => (
                            <Box
                                key={entry.announcementID}
                                onClick={() => handleOpenEntry(entry)}
                                sx={{
                                    display: 'flex', alignItems: 'flex-start', gap: 1.25,
                                    px: 2, py: 1.25, cursor: 'pointer',
                                    borderBottom: `1px solid ${announcementColors.border}`,
                                    '&:hover': { backgroundColor: HOVER_BG },
                                    // The row action stays out of sight until the pointer is
                                    // already on the row; a permanent button on every row
                                    // competes with the title for attention.
                                    '&:hover .row-action': { opacity: 1 },
                                    ...(!entry.seen && {
                                        boxShadow: `inset 2px 0 0 ${announcementColors.link}`,
                                    }),
                                }}
                            >
                                {/* Leading capsule: the row says what it is before a single
                                    word is read. Filled while unread, outlined once read. */}
                                <Box
                                    sx={{
                                        width: 30, height: 30, flexShrink: 0, mt: '2px',
                                        borderRadius: '9px',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        background: entry.seen
                                            ? announcementColors.surfaceMuted
                                            : ANNOUNCEMENT_BRAND_SURFACE,
                                        border: entry.seen ? `1px solid ${announcementColors.border}` : 'none',
                                    }}
                                >
                                    <IconSeeker
                                        icon="bullhorn"
                                        sx={{ fontSize: 12, color: entry.seen ? '#9CA3AF' : '#fff' }}
                                    />
                                </Box>

                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography
                                        sx={{
                                            fontSize: 13.5, lineHeight: 1.35,
                                            fontWeight: entry.seen ? 400 : 600,
                                            color: announcementColors.inkStrong,
                                        }}
                                    >
                                        {entry.title}
                                    </Typography>

                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
                                        <Typography
                                            // Relative to place it in the list; the exact day
                                            // one hover away, for when it is the one that matters.
                                            title={format(new Date(entry.startAt), 'dd/MM/yyyy')}
                                            sx={{ fontSize: 11, color: '#9CA3AF' }}
                                        >
                                            {formatRelativeDate(new Date(entry.startAt))}
                                        </Typography>

                                        {!entry.seen && (
                                            <Typography
                                                className="row-action"
                                                onClick={event => {
                                                    // Without this the row would open the
                                                    // announcement, which is the opposite of
                                                    // clearing it without reading it.
                                                    event.stopPropagation()
                                                    markSeen(entry.announcementID)
                                                }}
                                                sx={{
                                                    fontSize: 11, fontWeight: 600, cursor: 'pointer',
                                                    color: announcementColors.linkHover,
                                                    opacity: 0, transition: 'opacity .15s ease',
                                                    '&:hover': { textDecoration: 'underline' },
                                                }}
                                            >
                                                Mark as read
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                ))}
            </PopoverSeeker>

            {/* The same dialog the popup uses: an announcement must not look like two
                different things depending on where it was opened from. */}
            <AnnouncementDialog
                isOpen={!!reading}
                onClose={() => setReading(null)}
                title={reading?.title}
                date={reading?.startAt}
                cancelProps={{ sx: { display: 'none' } }}
                successText="Done"
                successProps={{ onClick: () => setReading(null) }}
            >
                <AnnouncementFrame html={reading?.body} title={reading?.title} height={200} />
            </AnnouncementDialog>
        </>
    )
}
